import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Link from '@atlaskit/link';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { ShortcutVisualizer } from './ShortcutVisualizer.tsx';

const i18n = defineMessages({
	deprecatedShortcutFlagTitle: {
		id: 'shortcuts.deprecated-shortcut-flag.title',
		defaultMessage: 'This shortcut is about to change',
		description:
			'Title of a flag that displays when the user activates a keyboard shortcut that will be removed in the future.',
	},
	deprecatedShortcutFlagDescription: {
		id: 'shortcuts.deprecated-shortcut-flag.description',
		defaultMessage:
			'Shortcuts are switching to a <supportLink>universal format</supportLink>. <inline>{oldShortcut} will now be {newShortcut}</inline>',
		description:
			'Description of a flag that displays when the user activates a keyboard shrotcut that will be removed soon.  {oldShortcut} and {newShortcut} will be representations of the keyboard shortcuts.  For example, "A" or "Cmd S".',
	},
	remindMeLaterButton: {
		id: 'shortcuts.deprecated-shortcut-flag.button.remind-me-later',
		defaultMessage: 'Remind me later',
		description:
			'Text for a button that hides the DeprecatedShortcutFlag for 7 days.  After 7 days, if a deprecated shortcut is used again, the flag will appear and remind the user that these keyboard shortcuts are going away.',
	},
	dontTellMeAgainButton: {
		id: 'shortcuts.deprecated-shortcut-flag.button.dont-tell-me-again',
		defaultMessage: 'Don’t tell me again',
		description:
			'Text for a button that hides the DeprecatedShortcutFlag permanently.  After clicking this, the user will no longer be prompted about these keyboard shortcuts going away.',
	},
});

const KEYBOARD_SHORTCUTS_SUPPORT_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/keyboard-shortcuts-markdown-and-autocomplete/';

const shortcutSentenceCss = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.025'),
});
const getShortcutDescription = ({ oldShortcut, newShortcut, acceleratorTriggered }) => {
	let newS = Array.isArray(newShortcut) ? newShortcut[0] : newShortcut;

	// ShortcutListener allows for multiple accelerators to be specified for one callback.  Ex. ["a", "mod+9"].
	// If any of those are triggerred, the callback will activate.
	// This makes it a little tricky to figure out what accelerator to recommend as a replacement.
	// Ex. deprecatedAccelerator: ["a", "b"], accelerator: ["mod+a", "mod+b"]
	// - If multiple accelerators are available in `newShortcut`, we default to the first one.
	// - However, if an equal number of accelerators are available in `oldShortcut` and `newShortcut`,
	//   then we look for the accelerator corresponding with the one that was actually triggered,
	//   based on array index.
	if (
		Array.isArray(oldShortcut) &&
		Array.isArray(newShortcut) &&
		oldShortcut.length === newShortcut.length
	) {
		const triggeredIndex = oldShortcut.indexOf(acceleratorTriggered);
		if (triggeredIndex >= 0 && triggeredIndex < newShortcut.length) {
			newS = newShortcut[triggeredIndex];
		}
	}

	return (
		<FormattedMessage
			{...i18n.deprecatedShortcutFlagDescription}
			values={{
				supportLink: (chunks) => (
					<Link target="_blank" href={KEYBOARD_SHORTCUTS_SUPPORT_LINK}>
						{chunks}
					</Link>
				),
				oldShortcut: <ShortcutVisualizer shortcut={acceleratorTriggered} />,
				newShortcut: <ShortcutVisualizer shortcut={newS} />,
				inline: (chunks) => <div css={shortcutSentenceCss}>{chunks}</div>,
			}}
		/>
	);
};

const FOREVER = 'forever';
const DEPRECATED_SHORTCUT_FLAG_ID = 'deprecatedShortcutFlag';

const shouldShowDeprecationFlag = () => {
	const hiddenUntilTime = localStorage.getItem(keys.UNIVERSAL_SHORTCUTS_HIDE_PROMPT_UNTIL) || '';
	if (hiddenUntilTime === FOREVER) {
		return false;
	} else if (hiddenUntilTime !== '') {
		const hiddenUntilTimeInMs = Number(hiddenUntilTime);
		if (!isNaN(hiddenUntilTimeInMs)) {
			return Date.now() > hiddenUntilTimeInMs;
		}
	}
	return true;
};

type MaybeShowDeprecatedShortcutFlagProps = {
	/**
	 * The accelerator (shortcut) that was actually pressed.
	 */
	acceleratorTriggered: string;
	/**
	 * The new accelerator (shortcut).
	 */
	accelerator: string | string[];
	/**
	 * The old accelerator (shortcut).
	 */
	deprecatedAccelerator: string | string[];
	flags: FlagsStateContainer;
};

/**
 * Shows the educational flag when the deprecated accelerator (shortcut) is used,
 * mentioning the new accelerator (shortcut) as a replacement.
 */
export const maybeShowDeprecatedShortcutFlag = ({
	flags,
	acceleratorTriggered,
	accelerator,
	deprecatedAccelerator,
}: MaybeShowDeprecatedShortcutFlagProps) => {
	if (shouldShowDeprecationFlag()) {
		void flags.showFlag({
			id: DEPRECATED_SHORTCUT_FLAG_ID,
			title: <FormattedMessage {...i18n.deprecatedShortcutFlagTitle} />,
			description: getShortcutDescription({
				oldShortcut: deprecatedAccelerator,
				newShortcut: accelerator,
				acceleratorTriggered,
			}),
			type: 'success',
			close: 'never', // Needed to bypass the flags.hideAllFlags() that happens on some page transitions.
			actions: [
				{
					content: <FormattedMessage {...i18n.remindMeLaterButton} />,
					onClick: () => {
						// Hide the prompt for 7 days.
						localStorage.setItem(
							keys.UNIVERSAL_SHORTCUTS_HIDE_PROMPT_UNTIL,
							Date.now() + 1000 * 60 * 60 * 24 * 7,
						);
						flags.hideFlag(DEPRECATED_SHORTCUT_FLAG_ID);
					},
				},
				{
					content: <FormattedMessage {...i18n.dontTellMeAgainButton} />,
					onClick: () => {
						localStorage.setItem(keys.UNIVERSAL_SHORTCUTS_HIDE_PROMPT_UNTIL, FOREVER);
						flags.hideFlag(DEPRECATED_SHORTCUT_FLAG_ID);
					},
				},
			],
		});
	}
};
